/**
 * Dashboard - Microsoft 365 Style
 * Main landing page with statistics and overview
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles, Text } from '@fluentui/react-components';
import {
  PeopleRegular,
  BuildingRegular,
  ShieldRegular,
  ClockRegular,
  PersonRegular,
  LocationRegular,
} from '@fluentui/react-icons';
import {
  DonutChart,
  IChartProps,
  VerticalBarChart,
  IVerticalBarChartDataPoint,
  LineChart,
  ILineChartPoints,
} from '@fluentui/react-charting';
import { ContentCard, StatCard, CardGrid } from '../../components/layout';
import { ApiClient } from '../../configurations/index.config';
import type { Employee, Client, Department } from '../../types';

const useStyles = makeStyles({
  dashboard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    marginBottom: '24px',
    display: 'block',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '16px',
  },
  welcomeCard: {
    padding: '32px',
    background: 'linear-gradient(135deg, #008272 0%, #00695c 100%)',
    color: '#ffffff',
    borderRadius: '8px',
  },
  welcomeTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '4px',
    display: 'block',
  },
  welcomeText: {
    fontSize: '14px',
    color: 'rgba(255, 255, 255, 0.9)',
    display: 'block',
  },
  quickActionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    marginTop: '16px',
  },
  quickActionButton: {
    padding: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '4px',
    border: 'none',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'all 0.2s',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
    },
  },
});

interface DashboardStats {
  totalEmployees: number;
  totalClients: number;
  activeSites: number;
  activeGuards: number;
  totalRoles: number;
  totalDepartments: number;
}

// Unused interfaces - kept for future chart implementation
// interface DepartmentData {
//   id: string;
//   department_name: string;
//   employee_count?: number;
// }

// interface ClientCategoryData {
//   id: string;
//   name: string;
//   client_count?: number;
// }

export const Dashboard: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalEmployees: 0,
    totalClients: 0,
    activeSites: 0,
    activeGuards: 0,
    totalRoles: 0,
    totalDepartments: 0,
  });
  const [loading, setLoading] = useState(true);
  const [departmentChartData, setDepartmentChartData] = useState<IChartProps[]>([]);
  const [clientChartData, setClientChartData] = useState<IVerticalBarChartDataPoint[]>([]);
  const [activityChartData, setActivityChartData] = useState<ILineChartPoints[]>([]);

  useEffect(() => {
    fetchDashboardStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  const processChartData = (
    employees: Employee[],
    departments: Department[],
    clients: Client[]
  ) => {
    // Department Distribution Donut Chart
    const deptMap = new Map<string, number>();
    employees.forEach(emp => {
      const dept = emp.department_name || 'Unassigned';
      deptMap.set(dept, (deptMap.get(dept) || 0) + 1);
    });

    const colors = ['#008272', '#00695c', '#004d40', '#6a1b9a', '#4a148c', '#1565c0', '#0d47a1'];
    const donutData: IChartProps[] = Array.from(deptMap.entries()).map(([dept, count], index) => ({
      chartTitle: dept,
      chartData: [
        {
          legend: dept,
          data: count,
          color: colors[index % colors.length],
        },
      ],
    }));
    setDepartmentChartData(donutData);

    // Client Category Bar Chart
    const categoryMap = new Map<string, number>();
    clients.forEach(client => {
      const category = client.category_name || 'Uncategorized';
      categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
    });

    const barData: IVerticalBarChartDataPoint[] = Array.from(categoryMap.entries()).map(
      ([category, count]) => ({
        x: category,
        y: count,
        color: '#008272',
      })
    );
    setClientChartData(barData);

    // Activity Trend Line Chart (Last 7 days - mock data)
    const lineData: ILineChartPoints[] = [
      {
        legend: 'Active Users',
        data: Array.from({ length: 7 }, (_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - (6 - i));
          return {
            x: date,
            y: Math.floor(Math.random() * 30) + 20, // Mock data
          };
        }),
        color: '#008272',
      },
    ];
    setActivityChartData(lineData);
  };

  const fetchDashboardStats = async () => {
    setLoading(true);
    try {
      // Fetch employees
      const employeesRes = await ApiClient.get('/get-employee-data');
      const employees = employeesRes.data?.data || [];

      // Fetch clients
      const clientsRes = await ApiClient.get('/get-clients');
      const clients = clientsRes.data?.data || [];

      // Fetch sites
      const sitesRes = await ApiClient.get('/get-sites');
      const sites = sitesRes.data?.data || [];

      // Fetch roles
      const rolesRes = await ApiClient.get('/get-roles');
      const roles = rolesRes.data?.data || [];

      // Fetch departments
      const deptsRes = await ApiClient.get('/get-departments');
      const departments = deptsRes.data?.data || [];

      // Fetch guard assignments
      const assignmentsRes = await ApiClient.get('/get-assign-guards');
      const assignments = assignmentsRes.data?.data || [];
      const activeGuards = assignments.filter(
        (a: { status: string }) => a.status === 'active'
      ).length;

      setStats({
        totalEmployees: employees.length,
        totalClients: clients.length,
        activeSites: sites.length,
        activeGuards: activeGuards,
        totalRoles: roles.length,
        totalDepartments: departments.length,
      });

      // Process chart data
      processChartData(employees, departments, clients);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
    setLoading(false);
  };

  return (
    <div className={styles.dashboard}>
      {/* Welcome Section */}
      <div className={styles.welcomeCard}>
        <Text className={styles.welcomeTitle}>Welcome to Security Admin Center</Text>
        <Text className={styles.welcomeText}>
          Manage your security personnel, clients, sites, and operations all in one place.
        </Text>

        {/* Quick Actions */}
        <div className={styles.quickActionsGrid}>
          <button
            className={styles.quickActionButton}
            onClick={() => navigate('/account/employees')}
          >
            + Add Employee
          </button>
          <button className={styles.quickActionButton} onClick={() => navigate('/account/clients')}>
            + Add Client
          </button>
          <button
            className={styles.quickActionButton}
            onClick={() => navigate('/account/guard-operations/assign-guard')}
          >
            + Assign Guard
          </button>
          <button
            className={styles.quickActionButton}
            onClick={() => navigate('/account/clocking-activity')}
          >
            View Clock-ins
          </button>
        </div>
      </div>

      {/* Statistics Overview */}
      <div>
        <Text className={styles.sectionTitle}>Overview</Text>
        <CardGrid columns={3}>
          <StatCard
            icon={<PeopleRegular />}
            label="Total Employees"
            value={loading ? '...' : stats.totalEmployees}
            change={{ value: 'All active personnel', positive: true }}
            onClick={() => navigate('/account/employees')}
          />
          <StatCard
            icon={<ShieldRegular />}
            label="Active Guards"
            value={loading ? '...' : stats.activeGuards}
            change={{ value: 'On duty personnel', positive: true }}
            onClick={() => navigate('/account/guard-operations/assign-guard')}
          />
          <StatCard
            icon={<BuildingRegular />}
            label="Total Clients"
            value={loading ? '...' : stats.totalClients}
            change={{ value: 'Registered clients', positive: true }}
            onClick={() => navigate('/account/clients')}
          />
          <StatCard
            icon={<LocationRegular />}
            label="Active Sites"
            value={loading ? '...' : stats.activeSites}
            change={{ value: 'Monitored locations', positive: true }}
            onClick={() => navigate('/account/sites')}
          />
          <StatCard
            icon={<PersonRegular />}
            label="Roles"
            value={loading ? '...' : stats.totalRoles}
            change={{ value: 'Defined roles', positive: true }}
            onClick={() => navigate('/account/roles')}
          />
          <StatCard
            icon={<BuildingRegular />}
            label="Departments"
            value={loading ? '...' : stats.totalDepartments}
            change={{ value: 'Organizational units', positive: true }}
            onClick={() => navigate('/account/departments')}
          />
        </CardGrid>
      </div>

      {/* Charts Section */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '24px',
        }}
      >
        {/* Employee Distribution by Department */}
        <ContentCard title="Employee Distribution" subtitle="By department">
          {departmentChartData.length > 0 ? (
            <DonutChart
              data={{
                chartTitle: 'Employees',
                chartData: departmentChartData.flatMap(d => d.chartData || []),
              }}
              innerRadius={55}
              legendsOverflowText="More"
              hideLegend={false}
              height={220}
              valueInsideDonut={stats.totalEmployees.toString()}
            />
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#605e5c' }}>
              <Text>No data available</Text>
            </div>
          )}
        </ContentCard>

        {/* Clients by Category */}
        <ContentCard title="Client Distribution" subtitle="By category">
          {clientChartData.length > 0 ? (
            <VerticalBarChart
              data={clientChartData}
              height={220}
              barWidth={32}
              yAxisTickCount={5}
            />
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#605e5c' }}>
              <Text>No data available</Text>
            </div>
          )}
        </ContentCard>
      </div>

      {/* Activity Trend */}
      <ContentCard title="Activity Trend" subtitle="Last 7 days">
        {activityChartData.length > 0 ? (
          <LineChart
            data={{
              lineChartData: activityChartData,
            }}
            legendsOverflowText="More"
            yAxisTickCount={5}
            height={250}
            allowMultipleShapesForPoints={false}
          />
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: '#605e5c' }}>
            <ClockRegular style={{ fontSize: '48px', marginBottom: '12px' }} />
            <Text>Loading activity data...</Text>
          </div>
        )}
      </ContentCard>

      {/* System Information */}
      <ContentCard title="System Information" subtitle="Application status and details">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
          }}
        >
          <div>
            <Text style={{ fontSize: '12px', color: '#605e5c', display: 'block' }}>Status</Text>
            <Text style={{ fontSize: '16px', fontWeight: '600', color: '#107c10' }}>● Online</Text>
          </div>
          <div>
            <Text style={{ fontSize: '12px', color: '#605e5c', display: 'block' }}>Version</Text>
            <Text style={{ fontSize: '16px', fontWeight: '600' }}>1.0.0</Text>
          </div>
          <div>
            <Text style={{ fontSize: '12px', color: '#605e5c', display: 'block' }}>
              Last Updated
            </Text>
            <Text style={{ fontSize: '16px', fontWeight: '600' }}>
              {new Date().toLocaleDateString()}
            </Text>
          </div>
        </div>
      </ContentCard>
    </div>
  );
};

export default Dashboard;
